import { useEffect } from 'react';
import type { AdminStats, AdminStatsDay } from '@orbit-sync/shared-types';
import { Avatar } from '../../components/Avatar.js';
import { Icon, type IconName } from '../../components/Icon.js';
import { Skeleton } from '../../components/Skeleton.js';
import { api } from '../../lib/api/index.js';
import { formatBytes } from '../../chat/labels.js';
import { useAdminQuery } from '../useAdminQuery.js';

/** The dashboard refreshes itself this often; online counts move by the minute. */
const REFRESH_MS = 15_000;

const NUMBER = new Intl.NumberFormat();
const DAY_LABEL = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});
const TIME = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** The BI overview: who is here, how busy the system is, and its health. */
export function AdminOverviewPage() {
  const { data, error, loading, reload } = useAdminQuery(
    (signal) => api.admin.stats(signal),
    'stats',
  );

  useEffect(() => {
    const timer = setInterval(reload, REFRESH_MS);
    return () => clearInterval(timer);
  }, [reload]);

  return (
    <section className="admin-page">
      <header className="admin-head">
        <div>
          <h1>Overview</h1>
          <p className="muted small">
            {data
              ? `Updated ${TIME.format(new Date(data.generatedAt))} · refreshes every 15 seconds`
              : 'Loading…'}
          </p>
        </div>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={reload}
          disabled={loading}
        >
          {loading ? <span className="btn-spinner" /> : <Icon name="rotate" size={13} />}
          Refresh
        </button>
      </header>

      {error ? (
        <p className="form-banner" role="alert">
          <Icon name="alertCircle" size={15} />
          {error}
        </p>
      ) : null}

      {data ? <Dashboard stats={data} /> : <DashboardSkeleton />}
    </section>
  );
}

function Dashboard({ stats }: { stats: AdminStats }) {
  return (
    <>
      <div className="kpi-grid">
        <Kpi
          icon="users"
          label="Users"
          value={stats.users.total}
          detail={`+${stats.users.newLast7Days} in 7 days`}
        />
        <Kpi
          icon="zap"
          label="Online now"
          value={stats.users.online}
          detail={`${stats.realtime.sockets} open connection${stats.realtime.sockets === 1 ? '' : 's'}`}
          tone="ok"
        />
        <Kpi
          icon="messageSquare"
          label="Active chats (24h)"
          value={stats.channels.activeLast24h}
          detail={`of ${NUMBER.format(stats.channels.total)} channels`}
        />
        <Kpi
          icon="messagesEmpty"
          label="Messages (24h)"
          value={stats.messages.last24h}
          detail={`${NUMBER.format(stats.messages.last7d)} in 7 days`}
        />
        <Kpi
          icon="hash"
          label="Channels"
          value={stats.channels.total}
          detail={`${stats.channels.direct} direct · ${stats.channels.group} group`}
        />
        <Kpi
          icon="file"
          label="Files shared"
          value={stats.messages.fileShares}
          detail={`${stats.realtime.activeTransfers} held in memory`}
        />
      </div>

      <div className="admin-panel">
        <h2>Messages per day</h2>
        <ActivityChart days={stats.activity} />
      </div>

      <div className="admin-columns">
        <div className="admin-panel">
          <h2>Busiest channels · 7 days</h2>
          <RankList
            rows={stats.topChannels.map((channel) => ({
              id: channel.id,
              label: channel.name,
              detail: channel.type === 'direct' ? 'Direct' : 'Group',
              value: channel.messages,
            }))}
          />
        </div>
        <div className="admin-panel">
          <h2>Most active people · 7 days</h2>
          <RankList
            rows={stats.topSenders.map((user) => ({
              id: user.id,
              label: user.displayName,
              value: user.messages,
            }))}
          />
        </div>
      </div>

      <div className="admin-columns">
        <div className="admin-panel">
          <h2>Online now · {stats.onlineUsers.length}</h2>
          {stats.onlineUsers.length === 0 ? (
            <p className="muted small">Nobody is connected.</p>
          ) : (
            <ul className="online-list">
              {stats.onlineUsers.map((user) => (
                <li key={user.id}>
                  <Avatar name={user.displayName} id={user.id} size="sm" presence="online" />
                  <span className="truncate" dir="auto">
                    {user.displayName}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="admin-panel">
          <h2>System</h2>
          <dl className="system-list">
            <dt>Database</dt>
            <dd>
              <span className={`dot dot--${stats.server.db === 'up' ? 'ok' : 'bad'}`} />{' '}
              {stats.server.db}
            </dd>
            <dt>Uptime</dt>
            <dd>{formatUptime(stats.server.uptimeSeconds)}</dd>
            <dt>Memory</dt>
            <dd>{formatBytes(stats.server.memoryRssBytes)}</dd>
            <dt>Version</dt>
            <dd>
              {stats.server.version} · Node {stats.server.nodeVersion}
            </dd>
            <dt>Total messages</dt>
            <dd>{NUMBER.format(stats.messages.total)}</dd>
          </dl>
        </div>
      </div>
    </>
  );
}

function Kpi({
  icon,
  label,
  value,
  detail,
  tone,
}: {
  icon: IconName;
  label: string;
  value: number;
  detail: string;
  tone?: 'ok';
}) {
  return (
    <div className={tone ? `kpi kpi--${tone}` : 'kpi'}>
      <span className="kpi-label">
        <Icon name={icon} size={14} />
        {label}
      </span>
      <strong className="kpi-value">{NUMBER.format(value)}</strong>
      <span className="kpi-detail">{detail}</span>
    </div>
  );
}

/**
 * Fourteen bars, one per UTC day, scaled to the busiest. Plain SVG: one chart
 * does not justify a charting dependency. Each bar carries its exact count in a
 * tooltip and in its accessible name; the y axis marks only the maximum.
 */
function ActivityChart({ days }: { days: AdminStatsDay[] }) {
  const width = 700;
  const height = 180;
  const top = 16;
  const bottom = 24;
  const max = Math.max(1, ...days.map((day) => day.messages));
  const slot = width / Math.max(1, days.length);
  const barWidth = Math.min(32, slot * 0.6);
  const plot = height - top - bottom;

  return (
    <svg
      className="activity-chart"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`Messages per day over the last ${days.length} days, peaking at ${max}`}
    >
      <line x1={0} x2={width} y1={top} y2={top} className="chart-grid" />
      <text x={0} y={top - 4} className="chart-axis">
        {NUMBER.format(max)}
      </text>
      <line x1={0} x2={width} y1={top + plot} y2={top + plot} className="chart-base" />
      {days.map((day, index) => {
        const barHeight = (day.messages / max) * plot;
        const x = index * slot + (slot - barWidth) / 2;
        const label = `${DAY_LABEL.format(new Date(`${day.date}T00:00:00Z`))}: ${day.messages} message${day.messages === 1 ? '' : 's'}`;

        return (
          <g key={day.date}>
            <rect
              x={x}
              y={top + plot - barHeight}
              width={barWidth}
              height={Math.max(barHeight, day.messages > 0 ? 2 : 0)}
              rx={3}
              className={index === days.length - 1 ? 'chart-bar chart-bar--today' : 'chart-bar'}
            >
              <title>{label}</title>
            </rect>
            {index % 2 === days.length % 2 || index === days.length - 1 ? (
              <text x={x + barWidth / 2} y={height - 6} textAnchor="middle" className="chart-axis">
                {DAY_LABEL.format(new Date(`${day.date}T00:00:00Z`))}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

function RankList({
  rows,
}: {
  rows: { id: string; label: string; detail?: string; value: number }[];
}) {
  if (rows.length === 0) {
    return <p className="muted small">No messages in the last 7 days.</p>;
  }

  const max = Math.max(...rows.map((row) => row.value));

  return (
    <ol className="rank-list">
      {rows.map((row) => (
        <li key={row.id}>
          <span className="rank-label truncate" dir="auto">
            {row.label}
            {row.detail ? <em>{row.detail}</em> : null}
          </span>
          <span className="rank-bar" style={{ width: `${(row.value / max) * 100}%` }} />
          <span className="rank-value">{NUMBER.format(row.value)}</span>
        </li>
      ))}
    </ol>
  );
}

function DashboardSkeleton() {
  return (
    <div className="kpi-grid" aria-busy="true">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <div className="kpi" key={index}>
          <Skeleton width="50%" height={11} />
          <Skeleton width="35%" height={26} />
          <Skeleton width="60%" height={10} />
        </div>
      ))}
    </div>
  );
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);

  return days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}
